// Import the necessary components
import VerofreshRecord from './VerofreshRecord'
import { isNaN, map, get, toNumber } from 'lodash'

class LocationRecord extends VerofreshRecord {
  /**
   * The default radius for circular geofences is 243.839992 meters (which is
   * 800 feet to 6 decimal places) for historical reasons
   */
  static get defaultRadius () {
    return 243.839992
  }

  static get minRadius () {
    return 22.86
  }

  static get maxRadius () {
    return 487.68
  }

  /**
   * Returns true/false if the vehicle has GPS coordinates set
   * @return boolean
   */
  hasGpsCoordinates () {
    if (this.getLatitude() != null && this.getLatitude() != null) {
      return true
    }

    return false
  };

  hasZeroedGpsCoordinates () {
    return (this.getLatitude() === 0 && this.getLongitude() === 0)
  }

  /**
   * Returns a comma separated string of latitude and longitude (e.g.
   * 32.123445,-113.987654)
   *
   * @return string
   */
  getGpsCoordinatesAsString () {
    if (this.hasGpsCoordinates()) {
      return `${this.getLatitude().toFixed(6)},${this.getLongitude().toFixed(6)}`
    }
    return 'unknown'
  };

  /**
   * @return float | undefined
   */
  getLatitude () {
    const parsedLatitude = parseFloat(this.latitude)
    return !isNaN(parsedLatitude) ? parsedLatitude : undefined
  }

  /**
   * @return float | undefined
   */
  getLongitude () {
    const parsedLongitude = parseFloat(this.longitude)
    return !isNaN(parsedLongitude) ? parsedLongitude : undefined
  }

  /**
   * Get the coordinates as a Google Maps LatLng compatible object
   * @return {Object}
   */
  get googleMapsLatLng () {
    return {
      lat: this.getLatitude(),
      lng: this.getLongitude()
    }
  }

  /**
   * Get the polygon as a Google Maps compatible array of LatLng objects
   * @return {Array}
   */
  get googleMapsPolygon () {
    if (this.polygon) {
      return map(this.polygon, (pair) => {
        return { lat: pair[0], lng: pair[1] }
      })
    }
  }

  get polygon () {
    if (this.geofence_type !== 'polygon') {
      return undefined
    }

    return this.geofence.coordinates
  }

  /**
   * Return a flag representing whether or not this instance is a delivery
   * location
   *
   * @return boolean
   */
  isDeliveryLocation () {
    return this.type === 'delivery'
  }

  /**
   * Return a flag representing whether or not this instance is a dc.
   * @returns {boolean}
   */
  isDc () {
    return this.type === 'dc'
  }

  isIgnored () {
    return this.type === 'ignored'
  }

  /**
   * Return the icon (based on type) to be used to represent this location
   *
   * @return string
   */
  get icon () {
    switch (this.type) {
      case 'delivery':
      case 'ignored':
        return 'map-marker-alt'
      case 'dc':
        return 'building'
      default:
        return 'exclamation'
    }
  }

  /**
   * Alternate version of the above that gets the Material Design icon for Vuetify
   * @returns {string}
   */
  get vuetifyIcon () {
    switch (this.type) {
      case 'delivery':
        return 'mdi-map-marker'
      case 'ignored':
        return 'mdi-map-marker-off'
      case 'dc':
        return 'mdi-office-building'
      default:
        return 'mdi-exclamation'
    }
  }

  /**
   * @return float
   */
  getRadius (defaultRadius = LocationRecord.defaultRadius) {
    if (get(this, 'geofence.radius')) {
      return toNumber(get(this, 'geofence.radius'))
    }

    return defaultRadius
  }

  setRadius (radius) {
    this.geofence = {
      type: 'circle',
      radius: radius
    }
  }

  /**
   * Returns a string identifying the kind of geofence that defines the bounds
   * of this location (e.g. polygon or circle)
   */
  /* eslint-disable-next-line camelcase */
  get geofence_type () {
    return get(this, 'geofence.type', 'circle')
  }

  fetchCustomersRelations () {
    return this._mapper()
      .datastore
      .getMapper('Location')
      .fetchCustomersRelations({
        id: this.id
      })
  }

  updateCustomers (customers) {
    this.customers = []

    customers.forEach(customerRelation => {
      const customer = this._mapper()
        .datastore
        .getMapper('Customer')
        .createRecord(customerRelation.customer)

      this.customers.push(customer)

      if (customerRelation.primary_customer) {
        this.customer = customer
        this.primary_customer = customer
      }
    })
  }

  /**
   *
   * @param int customerId
   * @param boolean primaryCustomer
   *
   * @return promise
   */
  linkCustomer (customerId, primaryCustomer = false) {
    return this._mapper()
      .datastore
      .getMapper('Location')
      .linkCustomer({
        id: this.id,
        data: {
          customer_id: customerId,
          location_id: this.id,
          primary_customer: primaryCustomer
        }
      })
  }

  /**
   *
   * @param int customerId
   *
   * @return promise
   */
  unlinkCustomer (customerId) {
    return this._mapper()
      .datastore
      .getMapper('Location')
      .unlinkCustomer({
        customer_id: customerId,
        id: this.id
      })
  }

  setPrimaryCustomer (customerId) {
    return this._mapper()
      .datastore
      .getMapper('Location')
      .updateCustomerRelation({
        customer_id: customerId,
        id: this.id,
        data: { primary_customer: true }
      })
  }
}

// Actions allow us to defined additional calls we can make to related
// endpoints.
const actions = {
  fetchCustomersRelations: {
    method: 'GET',
    getEndpoint: function (BaseMapper, options) {
      const basePath = BaseMapper.getAdapter(BaseMapper.defaultAdapter).basePath
      const endpoint = BaseMapper.endpoint
      return `${basePath}/${endpoint}/${options.id}/customer?expand=customer`
    },
    response (response) {
      return response.data
    }
  },
  linkCustomer: {
    method: 'POST',
    getEndpoint: function (BaseMapper, options) {
      const basePath = BaseMapper.getAdapter(BaseMapper.defaultAdapter).basePath
      const endpoint = BaseMapper.endpoint
      return `${basePath}/${endpoint}/${options.id}/customer`
    },
    response (response) {
      return response.data
    }
  },
  unlinkCustomer: {
    method: 'DELETE',
    getEndpoint: function (BaseMapper, options) {
      const basePath = BaseMapper.getAdapter(BaseMapper.defaultAdapter).basePath
      const endpoint = BaseMapper.endpoint
      return `${basePath}/${endpoint}/${options.id}/customer/${options.customer_id}`
    },
    response (response) {
      return response.data
    }
  },
  updateCustomerRelation: {
    method: 'PATCH',
    getEndpoint: function (BaseMapper, options) {
      const basePath = BaseMapper.getAdapter(BaseMapper.defaultAdapter).basePath
      const endpoint = BaseMapper.endpoint
      return `${basePath}/${endpoint}/${options.id}/customer/${options.customer_id}`
    },
    response (response) {
      return response.data
    }
  }
}

export default LocationRecord
export { actions }
